import { Plus, Trash2, Clock } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthSessionContext } from "../routing/auth-session-store";
import type { ServiceProviderResponse } from "../../core/types/services";
import type {
	Item,
	QuotePreferredSchedule,
} from "../../core/types/tsx/components/quote/QuoteEditor.types";
import { AlertModal, type AlertVariant } from "../modals/AlertModal";
import { ConfirmModal, type ConfirmVariant } from "../modals/ConfirmModal";
import {
	useRegisterUser,
	useSendQuotationMessage,
} from "../../hooks/control/useMessage";
import { useQuotationsByProvider } from "../../hooks/services/useQuotation";
import { useFetch } from "../../hooks/useFetch";

type QuoteEditorProps = {
	categoryName?: string;
	initialServiceName?: string;
	onServicesChange?: (services: string[]) => void;
	isQuotationLocked?: boolean;
	requestId?: number;
	providerId?: number;
	orderId?: number;
};

type ServicesByProviderPayload =
	| ServiceProviderResponse[]
	| {
			services?: ServiceProviderResponse[];
			items?: ServiceProviderResponse[];
	  };

const DEFAULT_INITIAL_UNIT_PRICE = 300;

const categoryServiceMap: Record<string, string[]> = {
	Plomería: ["Reparación de fuga", "Instalación de grifería", "Destape de tuberías"],
	Electricidad: [
		"Instalación de lámparas",
		"Reparación de cableado",
		"Mantenimiento eléctrico",
	],
	Carpintería: ["Armado de muebles", "Reparación de puertas", "Instalación de repisas"],
	Pintura: ["Pintura interior", "Pintura exterior", "Retoque de paredes"],
	Limpieza: ["Limpieza profunda", "Limpieza post-obra", "Limpieza de oficina"],
};

const normalizeLookupKey = (value: string) =>
	value
		.toLowerCase()
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.replace(/[^a-z0-9\s]/g, "")
		.replace(/\s+/g, " ")
		.trim();

const toArray = <T,>(value: T | T[] | null | undefined): T[] => {
	if (Array.isArray(value)) return value;
	if (value === null || value === undefined) return [];
	return [value];
};

const extractServiceCategoryNames = (service: ServiceProviderResponse) => {
	const currentRelations = toArray(
		(
			service as {
				categories_services_providers?:
					| ServiceProviderResponse["categories_services_providers"]
					| ServiceProviderResponse["categories_services_providers"][number]
					| null;
			}
		).categories_services_providers,
	);

	const currentNames = currentRelations
		.map((relation) =>
			relation?.categories_providers?.categories_services?.name_category?.trim(),
		)
		.filter((name): name is string => Boolean(name));

	if (currentNames.length > 0) {
		return Array.from(new Set(currentNames));
	}

	const legacyRelations = toArray(
		(
			service as {
				categoriesServices_servicesProviders?:
					| NonNullable<
							ServiceProviderResponse["categoriesServices_servicesProviders"]
					  >
					| NonNullable<
							ServiceProviderResponse["categoriesServices_servicesProviders"]
					  >[number]
					| null;
			}
		).categoriesServices_servicesProviders,
	);

	const legacyNames = legacyRelations
		.map((relation) => relation?.categoriesServices?.name_category?.trim())
		.filter((name): name is string => Boolean(name));

	return Array.from(new Set(legacyNames));
};

const extractProviderServices = (
	payload: ServicesByProviderPayload | null,
): ServiceProviderResponse[] => {
	if (Array.isArray(payload)) return payload;
	if (!payload || typeof payload !== "object") return [];
	if (Array.isArray(payload.services)) return payload.services;
	if (Array.isArray(payload.items)) return payload.items;
	return [];
};

const extractQuotationsArray = (raw: unknown): Array<Record<string, unknown>> => {
	if (Array.isArray(raw)) {
		return raw.filter((item): item is Record<string, unknown> =>
			Boolean(item && typeof item === "object"),
		);
	}

	if (!raw || typeof raw !== "object") return [];

	const candidate = raw as Record<string, unknown>;
	const list =
		(Array.isArray(candidate.items) && candidate.items) ||
		(Array.isArray(candidate.quotations) && candidate.quotations) ||
		(Array.isArray(candidate.data) && candidate.data) ||
		[];

	return list.filter((item): item is Record<string, unknown> =>
		Boolean(item && typeof item === "object"),
	);
};

const getTodayDateInputValue = () => {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const formatDateForSummary = (value: string) => {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
	if (!match) return value;
	const [, year, month, day] = match;
	return `${day}/${month}/${year}`;
};

const formatTimeByRegex = (rawValue: string): string => {
	const normalizedRaw = rawValue.trim();
	const colonMatch = /^(\d{1,2})(?::(\d{0,2}))?$/.exec(normalizedRaw);
	if (colonMatch) {
		const hourPart = colonMatch[1];
		const hour = Number(hourPart);
		if (!Number.isFinite(hour) || hour < 1 || hour > 12) {
			return hourPart[0] ?? "";
		}

		const minuteRaw = (colonMatch[2] ?? "").replace(/\D/g, "").slice(0, 2);
		if (!minuteRaw) return hourPart;
		if (minuteRaw.length === 1) return `${hourPart}:${minuteRaw}`;

		const minute = Number(minuteRaw);
		if (!Number.isFinite(minute) || minute > 59) {
			return `${hourPart}:${minuteRaw[0] ?? ""}`;
		}

		return `${hourPart}:${minuteRaw}`;
	}

	const digits = rawValue.replace(/\D/g, "").slice(0, 4);
	if (!digits) return "";

	if (digits.length === 1) {
		return digits;
	}

	if (digits.length === 2) {
		const hour = Number(digits);
		if (!Number.isFinite(hour) || hour < 1 || hour > 12) {
			return digits[0] ?? "";
		}
		return digits;
	}

	const hourPart = digits.slice(0, 2);
	const hour = Number(hourPart);
	if (!Number.isFinite(hour) || hour < 1 || hour > 12) {
		return digits[0] ?? "";
	}

	if (digits.length === 3) {
		return `${hourPart}:${digits.slice(2)}`;
	}

	const minutePart = digits.slice(2, 4);
	const minute = Number(minutePart);
	if (!Number.isFinite(minute) || minute > 59) {
		return `${hourPart}:${minutePart[0] ?? ""}`;
	}

	return `${hourPart}:${minutePart}`;
};

const normalizeTo12Hour = (rawTime: string) => {
	const masked = formatTimeByRegex(rawTime);
	if (!masked) return "";

	if (!masked.includes(":")) {
		const parsedHours = Number(masked);
		if (!Number.isFinite(parsedHours) || parsedHours < 1 || parsedHours > 12) {
			return masked;
		}
		return `${String(parsedHours).padStart(2, "0")}:00`;
	}

	const [hoursRaw = "", minutesRaw = ""] = masked.split(":");
	const parsedHours = Number(hoursRaw);
	const parsedMinutes = Number(minutesRaw);
	if (!Number.isFinite(parsedHours) || !Number.isFinite(parsedMinutes)) {
		return masked;
	}
	const normalizedHours = ((parsedHours % 12) + 12) % 12 || 12;
	const safeMinutes = Math.min(Math.max(parsedMinutes, 0), 59);
	return `${String(normalizedHours).padStart(2, "0")}:${String(safeMinutes).padStart(2, "0")}`;
};

const isComplete12Hour = (rawTime: string) =>
	/^((0[1-9])|(1[0-2])):[0-5][0-9]$/.test(rawTime.trim());

const formatHourForSummary = (
	rawTime: string,
	period: QuotePreferredSchedule["period"],
) => {
	const normalized = normalizeTo12Hour(rawTime);
	if (!isComplete12Hour(normalized)) return "";
	return `${normalized} ${period}`;
};

const isDateInputInPast = (value: string) => {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
	if (!match) return false;

	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);
	if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
		return false;
	}

	const selectedDate = new Date(year, month - 1, day);
	if (Number.isNaN(selectedDate.getTime())) return false;

	const today = new Date();
	today.setHours(0, 0, 0, 0);
	selectedDate.setHours(0, 0, 0, 0);
	return selectedDate < today;
};

export function QuoteEditor({
	categoryName,
	initialServiceName,
	onServicesChange,
	isQuotationLocked = false,
	providerId,
	orderId,
}: QuoteEditorProps) {
	const navigate = useNavigate();
	const { session } = useAuthSessionContext();
	const numericUserId = Number(session?.user?.id ?? 0);
	const { register, loading: registeringSocket } = useRegisterUser();
	const { sendQuotationMessage, loading: sendingQuotationMessage } =
		useSendQuotationMessage();
	const { execute: executeGetProviderServices } = useFetch<ServicesByProviderPayload>({
		endpoint: "/services-provider/provider/1",
		method: "GET",
		auto: false,
	});

	type ProviderServiceOption = {
		name: string;
		unitPrice: number;
		categoryNames: string[];
	};

	const [providerServiceOptions, setProviderServiceOptions] = useState<
		ProviderServiceOption[]
	>([]);
	const [items, setItems] = useState<Item[]>([
		{
			id: crypto.randomUUID(),
			service: initialServiceName?.trim() || "Mano de obra",
			quantity: 1,
			unitPrice: DEFAULT_INITIAL_UNIT_PRICE,
		},
	]);
	const [hours, setHours] = useState<number>(3);
	const [preferredSchedule, setPreferredSchedule] =
		useState<QuotePreferredSchedule>({
			date: getTodayDateInputValue(),
			time: "",
			period: "AM",
		});
	const [otherServiceByItemId, setOtherServiceByItemId] = useState<
		Record<string, string>
	>({});

	type AlertState = {
		open: boolean;
		title: string;
		message: string;
		variant: AlertVariant;
		confirmText?: string;
	};

	type AlertPayload = {
		title: string;
		message: string;
		variant?: AlertVariant;
		confirmText?: string;
	};

	type ConfirmState = {
		open: boolean;
		title: string;
		message: string;
		confirmLabel: string;
		cancelLabel: string;
		variant: ConfirmVariant;
		onConfirm: () => Promise<void> | void;
	};

	type ConfirmPayload = {
		title: string;
		message: string;
		confirmLabel?: string;
		cancelLabel?: string;
		variant?: ConfirmVariant;
		onConfirm: () => Promise<void> | void;
	};

	const [alertState, setAlertState] = useState<AlertState>({
		open: false,
		title: "",
		message: "",
		variant: "info",
	});

	const [confirmState, setConfirmState] = useState<ConfirmState>({
		open: false,
		title: "",
		message: "",
		confirmLabel: "Confirmar",
		cancelLabel: "Cancelar",
		variant: "primary",
		onConfirm: async () => undefined,
	});

	const showAlert = useCallback(
		({ title, message, variant = "info", confirmText }: AlertPayload) => {
			setAlertState({ open: true, title, message, variant, confirmText });
		},
		[],
	);

	const handleCloseAlert = useCallback(() => {
		setAlertState((prev) => ({ ...prev, open: false }));
	}, []);

	const openConfirm = useCallback(
		({
			title,
			message,
			confirmLabel = "Confirmar",
			cancelLabel = "Cancelar",
			variant = "primary",
			onConfirm,
		}: ConfirmPayload) => {
			setConfirmState({
				open: true,
				title,
				message,
				confirmLabel,
				cancelLabel,
				variant,
				onConfirm,
			});
		},
		[],
	);

	const handleCloseConfirm = useCallback(() => {
		setConfirmState((prev) => ({ ...prev, open: false }));
	}, []);

	const resolveServiceUnitPrice = useCallback(
		(serviceName: string): number | null => {
			const normalizedName = serviceName.trim().toLowerCase();
			if (!normalizedName) return null;

			const match = providerServiceOptions.find(
				(option) => option.name.trim().toLowerCase() === normalizedName,
			);

			if (!match) return null;
			return Number.isFinite(match.unitPrice) ? match.unitPrice : null;
		},
		[providerServiceOptions],
	);

	const normalizedCategoryName = useMemo(
		() => normalizeLookupKey(categoryName ?? ""),
		[categoryName],
	);

	const scopedProviderServiceOptions = useMemo(() => {
		if (providerServiceOptions.length === 0) return providerServiceOptions;
		if (!normalizedCategoryName) return providerServiceOptions;

		const hasCategoryMetadata = providerServiceOptions.some(
			(option) => option.categoryNames.length > 0,
		);
		if (!hasCategoryMetadata) return providerServiceOptions;

		const filtered = providerServiceOptions.filter((option) =>
			option.categoryNames.some(
				(category) => normalizeLookupKey(category) === normalizedCategoryName,
			),
		);

		return filtered.length > 0 ? filtered : providerServiceOptions;
	}, [providerServiceOptions, normalizedCategoryName]);

	const relatedServices = useMemo(() => {
		if (scopedProviderServiceOptions.length > 0) {
			const names = scopedProviderServiceOptions
				.map((option) => option.name)
				.filter((name) => name.trim().length > 0);
			const unique = Array.from(new Set(names));

			if (
				initialServiceName?.trim() &&
				!unique.some(
					(name) =>
						name.trim().toLowerCase() ===
						initialServiceName.trim().toLowerCase(),
				)
			) {
				unique.unshift(initialServiceName.trim());
			}

			return unique;
		}

		const options = categoryServiceMap[categoryName ?? ""] ?? [];
		if (options.length > 0) return options;
		return initialServiceName?.trim() ? [initialServiceName.trim()] : [];
	}, [categoryName, initialServiceName, scopedProviderServiceOptions]);

	useEffect(() => {
		if (providerServiceOptions.length === 0) return;

		setItems((prev) => {
			let changed = false;
			const next = prev.map((item) => {
				const mappedPrice = resolveServiceUnitPrice(item.service);
				if (typeof mappedPrice !== "number") return item;

				if (
					item.unitPrice !== 0 &&
					item.unitPrice !== DEFAULT_INITIAL_UNIT_PRICE
				) {
					return item;
				}

				if (item.unitPrice === mappedPrice) return item;

				changed = true;
				return { ...item, unitPrice: mappedPrice };
			});

			return changed ? next : prev;
		});
	}, [providerServiceOptions, resolveServiceUnitPrice]);

	const addItem = () => {
		setItems((prev) => [
			...prev,
			{ id: crypto.randomUUID(), service: "", quantity: 1, unitPrice: 0 },
		]);
	};

	const removeItem = (id: string) => {
		setItems((prev) => prev.filter((i) => i.id !== id));
		setOtherServiceByItemId((prev) => {
			const next = { ...prev };
			delete next[id];
			return next;
		});
	};

	const requestDeleteItem = (id: string) => {
		openConfirm({
			title: "Eliminar",
			message: "¿Deseas eliminar este ítem?",
			confirmLabel: "Sí",
			cancelLabel: "No",
			variant: "danger",
			onConfirm: () => {
				removeItem(id);
			},
		});
	};

	const updateItem = (id: string, patch: Partial<Item>) => {
		setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
	};

	const commitOtherService = (itemId: string) => {
		const normalized = (otherServiceByItemId[itemId] ?? "").trim();
		if (!normalized) return;
		const matchedPrice = resolveServiceUnitPrice(normalized);
		updateItem(itemId, {
			service: normalized,
			...(typeof matchedPrice === "number" ? { unitPrice: matchedPrice } : {}),
		});
		setOtherServiceByItemId((prev) => {
			const next = { ...prev };
			delete next[itemId];
			return next;
		});
	};

	useEffect(() => {
		if (!onServicesChange) return;
		onServicesChange(
			items
				.map((item) => item.service.trim())
				.filter((service) => service.length > 0),
		);
	}, [items, onServicesChange]);

	const subtotal = useMemo(
		() => items.reduce((acc, i) => acc + i.quantity * i.unitPrice, 0),
		[items],
	);
	const addedServicesCount = useMemo(
		() => items.filter((i) => i.service.trim().length > 0).length,
		[items],
	);
	const total = useMemo(() => Math.round(subtotal * 100) / 100, [subtotal]);

	const getQueryNumber = (key: string, fallback: number) => {
		try {
			const url = new URL(window.location.href);
			const v = url.searchParams.get(key);
			return v ? Number(v) : fallback;
		} catch {
			return fallback;
		}
	};

	const queryProviderId = getQueryNumber("providerId", 0);
	const queryOrderId = getQueryNumber("orderId", 0);
	const effectiveProviderId =
		typeof providerId === "number" && providerId > 0 ? providerId : queryProviderId;
	const effectiveOrderId =
		typeof orderId === "number" && orderId > 0 ? orderId : queryOrderId;
	const hasRequiredIds = effectiveProviderId > 0 && effectiveOrderId > 0;
	const { data: providerQuotationsRaw } = useQuotationsByProvider(
		effectiveProviderId,
	);
	const pendingQuotationIds = useMemo(() => {
		const ids = new Set<number>();
		extractQuotationsArray(providerQuotationsRaw).forEach((quotation) => {
			const quotationOrderId = Number(quotation.order_id ?? 0);
			if (!Number.isFinite(quotationOrderId) || quotationOrderId <= 0) return;
			if (quotationOrderId !== effectiveOrderId) return;

			const quotationId = Number(quotation.id ?? 0);
			if (!Number.isFinite(quotationId) || quotationId <= 0) return;

			const statusRaw = (quotation as { status_quotation?: unknown })
				?.status_quotation;
			const isActive =
				typeof statusRaw === "boolean" ? statusRaw : statusRaw !== false;
			if (!isActive) return;

			if (quotation.date_accepted || quotation.date_rejected) return;
			ids.add(quotationId);
		});
		return ids;
	}, [effectiveOrderId, providerQuotationsRaw]);
	const hasPendingQuotation = useMemo(() => {
		return pendingQuotationIds.size > 0;
	}, [pendingQuotationIds]);

	useEffect(() => {
		if (!effectiveProviderId) {
			setProviderServiceOptions([]);
			return;
		}

		let cancelled = false;

		const loadProviderServices = async () => {
			const endpoints = [
				`/services-provider/provider/${effectiveProviderId}?page=1&pageSize=500`,
				`/services-provider/provider/${effectiveProviderId}`,
			];

			for (const endpoint of endpoints) {
				try {
					const payload = await executeGetProviderServices({
						endpoint,
						method: "GET",
					});
					if (cancelled) return;

					const normalized = extractProviderServices(payload)
						.filter((service) => service.is_active !== false)
						.map((service) => ({
							name: String(service.name_service ?? "").trim(),
							unitPrice: Number(service.base_price ?? 0),
							categoryNames: extractServiceCategoryNames(service),
						}))
						.filter(
							(option) =>
								option.name.length > 0 &&
								Number.isFinite(option.unitPrice) &&
								option.unitPrice >= 0,
						);

					setProviderServiceOptions(normalized);
					return;
				} catch {
					// Try next endpoint variant.
				}
			}

			if (!cancelled) {
				setProviderServiceOptions([]);
			}
		};

		void loadProviderServices();

		return () => {
			cancelled = true;
		};
	}, [effectiveProviderId, executeGetProviderServices]);

	const submitQuotation = async () => {
		if (isQuotationLocked) {
			showAlert({
				title: "Pedido completado",
				message:
					"Este pedido ya está completado y no permite nuevas cotizaciones.",
				variant: "warning",
				confirmText: "Entendido",
			});
			return;
		}

		if (hasPendingQuotation) {
			showAlert({
				title: "Cotización pendiente",
				message:
					"Ya existe una cotización pendiente para este pedido. Debe ser aprobada o rechazada antes de enviar otra.",
				variant: "warning",
				confirmText: "Entendido",
			});
			return;
		}

		if (
			!Number.isFinite(effectiveProviderId) ||
			effectiveProviderId <= 0 ||
			!Number.isFinite(effectiveOrderId) ||
			effectiveOrderId <= 0
		) {
			showAlert({
				title: "Datos incompletos",
				message:
					"No se encontró el pedido o el proveedor para enviar la cotización.",
				variant: "error",
				confirmText: "OK",
			});
			return;
		}

		if (!Number.isFinite(numericUserId) || numericUserId <= 0) {
			showAlert({
				title: "Sesión inválida",
				message: "Debes iniciar sesión para enviar la cotización.",
				variant: "error",
				confirmText: "OK",
			});
			return;
		}

		const normalizedHours = Math.round(hours);
		if (!Number.isFinite(normalizedHours) || normalizedHours < 1) {
			showAlert({
				title: "Tiempo estimado requerido",
				message: "Ingresa al menos 1 hora como tiempo estimado.",
				variant: "warning",
				confirmText: "OK",
			});
			return;
		}

		if (!preferredSchedule.date.trim()) {
			showAlert({
				title: "Día estimado requerido",
				message: "Selecciona el día estimado del servicio.",
				variant: "warning",
				confirmText: "OK",
			});
			return;
		}

		if (isDateInputInPast(preferredSchedule.date)) {
			showAlert({
				title: "Día inválido",
				message: "No puedes seleccionar una fecha anterior a la actual.",
				variant: "warning",
				confirmText: "OK",
			});
			return;
		}

		const normalizedPreferredTime = normalizeTo12Hour(preferredSchedule.time);
		if (
			normalizedPreferredTime.trim() &&
			!isComplete12Hour(normalizedPreferredTime)
		) {
			showAlert({
				title: "Hora inválida",
				message:
					"Ingresa la hora preferida en formato 12 horas (hh:mm) y selecciona AM o PM.",
				variant: "warning",
				confirmText: "OK",
			});
			return;
		}

		const normalizedItems = items
			.map((item) => {
				const description = item.service.trim();
				const quantity = Number(item.quantity);
				const unitPrice = Number(item.unitPrice);
				const subtotalItem = Number(Math.round(quantity * unitPrice * 100) / 100);

				if (!description || !Number.isFinite(quantity) || quantity <= 0) {
					return null;
				}

				if (!Number.isFinite(unitPrice) || unitPrice < 0) {
					return null;
				}

				return {
					description_item: description,
					unit_price_item: unitPrice,
					quantity_item: quantity,
					subtotal_item: subtotalItem,
				};
			})
			.filter((item): item is NonNullable<typeof item> => Boolean(item));

		if (normalizedItems.length === 0) {
			showAlert({
				title: "Items incompletos",
				message:
					"Agrega al menos un ítem con servicio, cantidad y precio válidos.",
				variant: "warning",
				confirmText: "OK",
			});
			return;
		}

		const baseDescription =
			normalizedItems
				.map((item) => item.description_item)
				.filter(Boolean)
				.join("; ") || "Cotización de servicio";
		const scheduleDetails = [
			`Fecha estimada: ${formatDateForSummary(preferredSchedule.date)}`,
			...(normalizedPreferredTime.trim()
				? [
						`Hora estimada: ${formatHourForSummary(normalizedPreferredTime, preferredSchedule.period)}`,
					]
				: []),
		];
		const descriptionQuotation = `${baseDescription}\n${scheduleDetails.join(" | ")}`;

		try {
			await register(numericUserId);
			await sendQuotationMessage({
				reminent_id: numericUserId,
				order_id: effectiveOrderId,
				provider_id: effectiveProviderId,
				proposed_price: total,
				description_quotation: descriptionQuotation,
				estimated_time: normalizedHours,
				materials_included: false,
				items: normalizedItems,
			});

			showAlert({
				title: "Cotización enviada",
				message: "La cotización fue enviada al chat para aprobación del cliente.",
				variant: "success",
				confirmText: "OK",
			});
			navigate("/service/profile");
		} catch (e) {
			console.warn("Error al enviar cotización:", e);
			showAlert({
				title: "No se pudo enviar",
				message:
					e instanceof Error ? e.message : "No se pudo enviar la cotización",
				variant: "error",
				confirmText: "OK",
			});
		}
	};

	return (
		<>
			<section className='space-y-6 lg:col-span-2 lg:space-y-8'>
				{/* Desglose de Costos */}
				<div className='rounded-2xl p-1'>
					<div className='mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:pr-2'>
						<h3 className='text-base font-semibold text-text-primary'>
							Desglose de Costos
						</h3>
						<button
							type='button'
							onClick={addItem}
							className='inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-base font-semibold text-white shadow-md transition hover:bg-primary-hover sm:w-auto'
						>
							<Plus className='h-5 w-5' />
							Agregar Item
						</button>
					</div>
					{items.map((item, idx) => {
						const normalizedService = item.service.trim();
						const isRelatedService =
							relatedServices.includes(normalizedService);
						const hasCustomService =
							normalizedService.length > 0 && !isRelatedService;
						const serviceSelection =
							isRelatedService || hasCustomService
								? normalizedService
								: "Otro";
						const serviceOptions = hasCustomService
							? [...relatedServices, normalizedService]
							: relatedServices;

						return (
							<div
								key={item.id}
								className='mb-5 rounded-xl soft-border bg-(--bg) p-4 card-shadow sm:mb-6 sm:p-6'
							>
								<div className='mb-4 flex items-center justify-between text-sm font-semibold text-text-secondary'>
									<span>Item #{idx + 1}</span>
									{items.length > 1 && (
										<button
											type='button'
											className='text-rose-500 transition cursor-pointer hover:text-rose-600'
											aria-label='Eliminar'
											onClick={() => requestDeleteItem(item.id)}
										>
											<Trash2 className='h-4 w-4' />
										</button>
									)}
								</div>

								<div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
									<div>
										<label className='text-sm font-medium text-text-tertiary'>
											Servicio
										</label>
										<select
											value={serviceSelection}
											aria-label='Servicio'
											title='Servicio'
											onChange={(e) => {
												const selected = e.target.value;
												if (selected === "Otro") {
													updateItem(item.id, { service: "" });
													return;
												}
												const matchedPrice =
													resolveServiceUnitPrice(selected);
												updateItem(item.id, {
													service: selected,
													...(typeof matchedPrice === "number"
														? { unitPrice: matchedPrice }
														: {}),
												});
												setOtherServiceByItemId((prev) => {
													const next = { ...prev };
													delete next[item.id];
													return next;
												});
											}}
											className='mt-1 h-12 w-full rounded soft-border bg-white px-3 text-base text-text-secondary outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
										>
											{serviceOptions.map((option) => (
												<option key={option} value={option}>
													{option}
												</option>
											))}
											<option value='Otro'>Otro</option>
										</select>
										{serviceSelection === "Otro" ? (
											<input
												type='text'
												value={
													otherServiceByItemId[item.id] ?? ""
												}
												onChange={(e) =>
													setOtherServiceByItemId((prev) => ({
														...prev,
														[item.id]: e.target.value,
													}))
												}
												onKeyDown={(e) => {
													if (e.key !== "Enter") return;
													e.preventDefault();
													commitOtherService(item.id);
												}}
												onBlur={() => {
													commitOtherService(item.id);
												}}
												placeholder='Especifica el servicio'
												className='mt-2 h-12 w-full rounded soft-border bg-white px-3 text-base text-text-secondary outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
											/>
										) : null}
									</div>

									<div>
										<label className='text-sm font-medium text-text-tertiary'>
											Cantidad
										</label>
										<input
											type='number'
											min={1}
											aria-label='Cantidad'
											title='Cantidad'
											placeholder='1'
											value={item.quantity}
											onChange={(e) =>
												updateItem(item.id, {
													quantity: Number(e.target.value) || 0,
												})
											}
											className='mt-1 h-12 w-full rounded soft-border bg-white px-3 text-base text-text-secondary outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
										/>
									</div>

									<div>
										<label className='text-sm font-medium text-text-tertiary'>
											Precio Unit.
										</label>
										<div className='mt-1 flex h-12 w-full items-center rounded soft-border bg-white'>
											<span className='px-2 text-text-tertiary'>
												$
											</span>
											<input
												type='number'
												min={0}
												aria-label='Precio Unitario'
												title='Precio Unitario'
												placeholder='0'
												value={item.unitPrice}
												onChange={(e) =>
													updateItem(item.id, {
														unitPrice:
															Number(e.target.value) || 0,
													})
												}
												className='h-full w-full rounded-r px-2 text-base text-text-secondary outline-none'
											/>
										</div>
									</div>
								</div>

								<div className='mt-4 flex flex-col gap-1 border-t border-dashed border-slate-200 pt-4 text-sm sm:flex-row sm:items-center sm:justify-between sm:pt-6'>
									<span className='text-text-tertiary'>
										Total del Item
									</span>
									<span className='text-base font-semibold text-primary'>
										${(item.quantity * item.unitPrice).toFixed(2)}
									</span>
								</div>
							</div>
						);
					})}
				</div>

				{/* Resumen de Cotización */}
				<div className='rounded-2xl p-1'>
					<div className='space-y-4 rounded-xl bg-(--bg) p-4 text-base card-shadow sm:p-6'>
						<span className='text-base font-semibold block mb-7 text-text-primary'>
							Resumen de Cotización
						</span>
						<div className='flex items-center justify-between'>
							<span className='text-text-tertiary'>Subtotal</span>
							<span className='font-medium text-text-secondary'>
								${subtotal.toFixed(2)}
							</span>
						</div>
						{addedServicesCount > 0 ? (
							<div className='flex items-center justify-between'>
								<span className='text-text-tertiary'>
									Servicios agregados
								</span>
								<span className='font-medium text-text-secondary'>
									{addedServicesCount}
								</span>
							</div>
						) : null}
						<div className='flex items-center justify-between pt-2 border-t border-dashed border-slate-200'>
							<span className='text-text-tertiary'>Total</span>
							<span className='text-xl font-semibold text-primary sm:text-2xl'>
								${total.toFixed(2)}
							</span>
						</div>

						<div className='pt-6 mt-4'>
							<div className='flex items-center gap-2'>
								<Clock className='h-4 w-4 text-text-tertiary' />
								<label className='text-sm font-medium text-text-secondary'>
									Tiempo estimado de trabajo
								</label>
							</div>
							<div className='mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3'>
								<div>
									<label className='text-sm font-medium text-text-tertiary'>
										Día
									</label>
									<input
										type='date'
										aria-label='Día estimado de trabajo'
										title='Día estimado de trabajo'
										value={preferredSchedule.date}
										min={getTodayDateInputValue()}
										onChange={(e) =>
											setPreferredSchedule((prev) => ({
												...prev,
												date: e.target.value,
											}))
										}
										className='mt-1 h-11 w-full rounded soft-border bg-white px-3 text-base text-text-secondary outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
									/>
								</div>
								<div>
									<label className='text-sm font-medium text-text-tertiary'>
										Hora (opcional)
									</label>
									<div className='mt-1 grid grid-cols-[1fr_auto] gap-2'>
										<input
											type='text'
											inputMode='numeric'
											maxLength={5}
											placeholder='hh:mm'
											aria-label='Hora estimada de trabajo'
											title='Hora estimada de trabajo'
											value={formatTimeByRegex(preferredSchedule.time)}
											onChange={(e) =>
												setPreferredSchedule((prev) => ({
													...prev,
													time: formatTimeByRegex(e.target.value),
												}))
											}
											onBlur={() =>
												setPreferredSchedule((prev) => ({
													...prev,
													time: normalizeTo12Hour(prev.time),
												}))
											}
											className='h-11 w-full rounded soft-border bg-white px-3 text-base text-text-secondary outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
										/>
										<select
											aria-label='Periodo de hora estimada'
											title='Periodo de hora estimada'
											value={preferredSchedule.period}
											onChange={(e) =>
												setPreferredSchedule((prev) => ({
													...prev,
													period: e.target.value as QuotePreferredSchedule["period"],
												}))
											}
											className='select-with-arrow h-11 rounded soft-border bg-white pl-3 pr-8 text-sm font-medium text-text-secondary outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
										>
											<option value='AM'>AM</option>
											<option value='PM'>PM</option>
										</select>
									</div>
								</div>
								<div>
									<label className='text-sm font-medium text-text-tertiary'>
										Duración (horas)
									</label>
								<input
									type='number'
									min={1}
									aria-label='Tiempo estimado en horas'
									title='Tiempo estimado en horas'
									placeholder='0'
									value={hours}
									onChange={(e) =>
										setHours(Number(e.target.value) || 0)
									}
									className='mt-1 h-11 w-full rounded soft-border bg-white px-3 text-base text-text-secondary outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
								/>
								</div>
							</div>
						</div>

						<div className='pt-2'>
							<button
								type='button'
								onClick={submitQuotation}
								disabled={
									isQuotationLocked ||
									hasPendingQuotation ||
									!hasRequiredIds ||
									registeringSocket ||
									sendingQuotationMessage
								}
								className='cursor-pointer mt-2 w-full rounded-full bg-primary px-5 py-3 text-base font-semibold text-white shadow-md transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60'
							>
								{registeringSocket || sendingQuotationMessage
									? "Enviando..."
									: "Enviar Cotización"}
							</button>
							{!hasRequiredIds ? (
								<p className='mt-2 text-sm font-medium text-error-text'>
									Faltan datos de pedido/proveedor para enviar la
									cotización.
								</p>
							) : null}
							{hasPendingQuotation ? (
								<p className='mt-2 text-sm font-medium text-error-text'>
									Ya existe una cotización pendiente para este pedido.
									Debe ser aprobada o rechazada antes de enviar otra.
								</p>
							) : null}
							{isQuotationLocked ? (
								<p className='mt-2 text-sm font-medium text-error-text'>
									Este pedido ya está completado y no admite nuevas
									cotizaciones.
								</p>
							) : null}
						</div>
					</div>
				</div>

				{/* Notas y Condiciones */}
				<div className='mb-8 rounded-2xl p-1'>
					<div className='mb-3 text-base font-semibold text-text-primary'>
						Notas y Condiciones
					</div>
					<textarea
						rows={5}
						placeholder='Incluye términos, condiciones, garantías u cualquier información adicional relevante para el cliente...'
						className='w-full rounded-lg soft-border bg-white px-3 py-4 text-base text-text-secondary outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
					/>
				</div>
			</section>

			<AlertModal
				open={alertState.open}
				onClose={handleCloseAlert}
				title={alertState.title}
				message={alertState.message}
				variant={alertState.variant}
				confirmText={alertState.confirmText}
			/>
			<ConfirmModal
				open={confirmState.open}
				title={confirmState.title}
				message={confirmState.message}
				confirmLabel={confirmState.confirmLabel}
				cancelLabel={confirmState.cancelLabel}
				variant={confirmState.variant}
				onCancel={handleCloseConfirm}
				onConfirm={async () => {
					try {
						await confirmState.onConfirm();
					} finally {
						handleCloseConfirm();
					}
				}}
			/>
		</>
	);
}

